'use client';

import { useState } from 'react';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

function authHeaders(): Record<string, string> {
  const token    = localStorage.getItem('access_token') || localStorage.getItem('super_admin_token') || '';
  const tenantId = localStorage.getItem('tenant_id') || '';
  return {
    'Authorization': `Bearer ${token}`,
    'X-Tenant-Id':   tenantId,
    'Content-Type':  'application/json',
  };
}

async function fetchWithRefresh(url: string, init?: RequestInit): Promise<Response> {
  const headers = { ...authHeaders(), ...(init?.headers as Record<string, string> ?? {}) };
  const res = await fetch(url, { ...init, headers, credentials: 'include' });
  if (res.status !== 401) return res;

  const refreshTok = localStorage.getItem('refresh_token');
  if (refreshTok) {
    const refreshRes = await fetch(`${API_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshTok }),
    });
    if (refreshRes.ok) {
      const data = await refreshRes.json();
      localStorage.setItem('access_token', data.access_token);
      if (localStorage.getItem('user_type') === 'super_admin') {
        localStorage.setItem('super_admin_token', data.access_token);
      }
    }
  }
  return fetch(url, { ...init, headers: { ...authHeaders(), ...(init?.headers as Record<string, string> ?? {}) }, credentials: 'include' });
}

const POLICY_FIELDS: { key: string; label: string }[] = [
  { key: 'policy_number', label: 'Policy Number' },
  { key: 'insured_name', label: 'Insured Name' },
  { key: 'vehicle_reg', label: 'Vehicle Registration No' },
  { key: 'vehicle_make', label: 'Vehicle Make' },
  { key: 'vehicle_model', label: 'Vehicle Model' },
  { key: 'vehicle_body_type', label: 'Vehicle Body Type' },
  { key: 'cubic_capacity', label: 'Cubic Capacity' },
  { key: 'vehicle_type', label: 'Vehicle Type' },
  { key: 'engine_number', label: 'Engine Number' },
  { key: 'chassis_number', label: 'Chassis Number' },
  { key: 'insurer', label: 'Insurer' },
  { key: 'insurer_gst_number', label: 'Insurer GST Number' },
  { key: 'policy_type', label: 'Policy Type' },
  { key: 'coverage_type', label: 'Coverage Type' },
  { key: 'valid_from', label: 'Valid From' },
  { key: 'valid_until', label: 'Valid Until' },
  { key: 'sum_insured', label: 'Sum Insured (IDV)' },
  { key: 'excess_amount', label: 'Excess / Deductible (₹)' },
  { key: 'add_on_covers', label: 'Add-on Covers' },
  { key: 'financer_name', label: 'Financer / Lender Name' },
];

export function PolicyDetailsPanel({
  jobId, docId, extractedFields, savedFields, onSaved,
}: {
  jobId: string;
  docId: string;
  extractedFields: Record<string, unknown>;
  savedFields?: Record<string, unknown> | null;
  onSaved: () => void;
}) {
  // A previously-saved claim_policy_details record (savedFields) takes
  // precedence over a fresh, not-yet-saved Gemini extraction — otherwise
  // reopening this panel after Save would show the raw extraction again
  // instead of what was actually verified and persisted. This preference is
  // per FIELD, not all-or-nothing: a saved record predating a newly-added
  // field (e.g. engine_number/chassis_number added after the claim was first
  // saved) has that field as null, and must fall back to the fresh
  // extraction rather than showing blank despite the OCR having a value.
  const [form, setForm] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const { key } of POLICY_FIELDS) {
      const v = savedFields?.[key] ?? extractedFields[key];
      if (v != null && v !== '') initial[key] = String(v);
    }
    return initial;
  });
  const [verified, setVerified] = useState(Boolean(savedFields?.is_verified));
  const [isNilDepreciation, setIsNilDepreciation] = useState(
    Boolean(savedFields?.is_nil_depreciation ?? extractedFields.is_nil_depreciation)
  );
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  async function handleSave() {
    setSaving(true);
    setSaveErr(null);
    setSavedOk(false);
    try {
      const body: Record<string, string | boolean | number | null> = {
        policy_doc_id: docId,
        is_verified: verified,
        is_nil_depreciation: isNilDepreciation,
      };
      for (const { key } of POLICY_FIELDS) {
        const val = form[key];
        if (key === 'sum_insured' || key === 'excess_amount') {
          body[key] = val ? Number(val) : null;
        } else {
          body[key] = val || null;
        }
      }

      const res = await fetchWithRefresh(
        `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/policy-details`,
        { method: 'PATCH', body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail || `Save failed (${res.status})`);
      }
      setSavedOk(true);
      onSaved();
      setTimeout(() => setSavedOk(false), 3000);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <svg className="h-4 w-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-sm font-semibold text-emerald-900">Policy Details</h3>
        <span className="text-xs text-emerald-500 ml-auto">Saved to policy record</span>
      </div>

      <div className="space-y-3">
        {POLICY_FIELDS.map(f => (
          <div key={f.key}>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              {f.label}
            </label>
            <input
              type="text"
              value={form[f.key] ?? ''}
              onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
              placeholder="—"
              className="w-full rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
            />
          </div>
        ))}
      </div>

      {saveErr && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{saveErr}</p>
      )}

      <label className="flex items-start gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={isNilDepreciation}
          onChange={e => setIsNilDepreciation(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
        />
        <span className="text-xs text-gray-700 leading-relaxed">
          Nil Depreciation (Zero Depreciation) cover applies to this policy.
        </span>
      </label>

      <label className="flex items-start gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={verified}
          onChange={e => setVerified(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
        />
        <span className="text-xs text-gray-700 leading-relaxed">
          I have verified all policy information against the physical document and confirm it is accurate.
        </span>
      </label>

      <button
        onClick={handleSave}
        disabled={!verified || saving}
        className={`w-full flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          savedOk ? 'bg-green-600 hover:bg-green-700' : 'bg-emerald-600 hover:bg-emerald-700'
        }`}
      >
        {saving ? 'Saving…' : savedOk ? 'Policy Details Saved' : 'Save Policy Details'}
      </button>
      {!verified && (
        <p className="text-center text-xs text-gray-400">Tick the verification checkbox to enable save</p>
      )}
    </div>
  );
}
